import './Scrollytelling.scss'
import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react'

// the 3D scene (and Three.js) loads after the content, so the page appears at once
const SceneBackground = lazy(() => import('../Scene/SceneBackground'))

/*
 * Scrollytelling stage.
 *
 * A tall "runway" (one screen of scroll per panel) holds a sticky, full-screen
 * stage where every panel is stacked. Native scrolling moves through the
 * runway; the scroll position picks the active panel:
 *   - the next panel rises over the stage like a curtain,
 *   - the content of the previous one fades away upwards.
 * Content that is too tall for the screen is scaled down to fit, as a safety net.
 */

const REVEAL = [
    '.hero__content > *',
    '.services__header',
    '.services__card',
    '.approach__card',
    '.showcase__nav',
    '.showcase__stage',
    '.backoffice__text > *',
    '.backoffice__schema',
    '.process__container__header',
    '.process__item',
    '.audiences__header',
    '.audiences__list',
    '.testimonials__card',
    '.about__text > *',
    '.about__figure',
    '.contact__container > *',
    '.sectionEyebrow',
].join(',')

const MIN_FIT = 0.72
// a phone held sideways has little height: allow content to shrink a bit more
const LANDSCAPE_PHONE = '(orientation: landscape) and (max-height: 520px)'
const MIN_FIT_LANDSCAPE = 0.58

function Panel({ index, state, layer, children }) {
    const rootRef = useRef(null)
    const viewportRef = useRef(null)
    const contentRef = useRef(null)

    // staggered entrance: number the reveal targets of the panel
    useEffect(() => {
        const targets = rootRef.current.querySelectorAll(REVEAL)

        targets.forEach((el, i) => {
            el.setAttribute('data-reveal', '')
            el.style.setProperty('--d', i)
        })
    }, [children])

    // hidden panels must not be reachable with the keyboard / screen readers
    useEffect(() => {
        const el = rootRef.current

        if (state === 'active') {
            el.removeAttribute('inert')
            el.removeAttribute('aria-hidden')
        } else {
            el.setAttribute('inert', '')
            el.setAttribute('aria-hidden', 'true')
        }
    }, [state])

    // scale the content down when it does not fit in one screen
    useEffect(() => {
        const viewport = viewportRef.current
        const content = contentRef.current

        const fit = () => {
            content.style.width = '100%'
            content.style.transform = 'none'

            const breathing = parseFloat(getComputedStyle(viewport).paddingTop) || 0
            const available = viewport.clientHeight - 2 * breathing
            const needed = content.offsetHeight
            const floor = window.matchMedia(LANDSCAPE_PHONE).matches ? MIN_FIT_LANDSCAPE : MIN_FIT
            const scale = Math.max(floor, Math.min(1, available / needed))

            if (scale < 1) {
                content.style.width = `${100 / scale}%`
                content.style.transform = `scale(${scale})`
            }
        }

        fit()

        const observer = new ResizeObserver(fit)
        observer.observe(viewport)

        window.addEventListener('load', fit)
        if (document.fonts && document.fonts.ready) {
            document.fonts.ready.then(fit)
        }
        const timer = setTimeout(fit, 400)

        return () => {
            observer.disconnect()
            window.removeEventListener('load', fit)
            clearTimeout(timer)
        }
    }, [children])

    return (
        <div
            ref={rootRef}
            className={`panel panel--${state}`}
            data-index={index}
            style={{ zIndex: layer }}
        >
            <div ref={viewportRef} className="panel__viewport">
                <div ref={contentRef} className="panel__content">
                    {children}
                </div>
            </div>
        </div>
    )
}

function Scrollytelling({ panels }) {
    const count = panels.length
    const [active, setActive] = useState(0)
    const [ready, setReady] = useState(false)
    const stepRef = useRef(null)
    const progressRef = useRef(0)

    const stepHeight = () =>
        (stepRef.current && stepRef.current.offsetHeight) || window.innerHeight

    const goTo = useCallback(
        (index) => {
            const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

            window.scrollTo({
                top: Math.max(0, Math.min(count - 1, index)) * stepHeight(),
                behavior: reduced ? 'auto' : 'smooth',
            })
        },
        [count]
    )

    // the first panel animates in once the page is mounted
    useEffect(() => {
        const frame = requestAnimationFrame(() => setReady(true))
        return () => cancelAnimationFrame(frame)
    }, [])

    // scroll position -> active panel
    useEffect(() => {
        document.documentElement.classList.add('scrolly-on')

        let frame = 0
        const update = () => {
            frame = 0
            progressRef.current = window.scrollY / stepHeight()
            const index = Math.round(progressRef.current)
            setActive(Number.isFinite(index) ? Math.max(0, Math.min(count - 1, index)) : 0)
        }
        const onScroll = () => {
            if (!frame) frame = requestAnimationFrame(update)
        }

        update()
        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', onScroll)

        return () => {
            document.documentElement.classList.remove('scrolly-on')
            window.removeEventListener('scroll', onScroll)
            window.removeEventListener('resize', onScroll)
            if (frame) cancelAnimationFrame(frame)
        }
    }, [count])

    // in-page anchors (e.g. #realisations) scroll to the panel that holds them
    useEffect(() => {
        const onClick = (event) => {
            const link = event.target.closest && event.target.closest('a[href^="#"]')
            if (!link) return

            const id = link.getAttribute('href').slice(1)
            const target = id && document.getElementById(id)
            const panel = target && target.closest('.panel')
            if (!panel) return

            event.preventDefault()
            goTo(Number(panel.dataset.index))
        }

        document.addEventListener('click', onClick)
        return () => document.removeEventListener('click', onClick)
    }, [goTo])

    const stateOf = (index) => {
        if (!ready) return 'upcoming'
        if (index === active) return 'active'
        return index < active ? 'past' : 'upcoming'
    }

    return (
        <div className="scrolly" style={{ '--panels': count }}>
            <div className="scrolly__stage">
                {/* layer 1: the coloured backgrounds, which wipe up like a curtain */}
                {panels.map((panel, index) => (
                    <div
                        key={`bg-${panel.id}`}
                        className={`scrolly__bg scrolly__bg--${stateOf(index)} scrolly__bg--${panel.theme}`}
                        style={{ zIndex: index + 1 }}
                        aria-hidden="true"
                    />
                ))}

                {/* layer 2: the 3D shape of the current section */}
                <Suspense fallback={null}>
                    <SceneBackground
                        sceneKey={(panels[active] || panels[0]).scene}
                        theme={(panels[active] || panels[0]).theme}
                        progressRef={progressRef}
                        style={{ zIndex: count + 1 }}
                    />
                </Suspense>

                {/* layer 3: the content */}
                {panels.map((panel, index) => (
                    <Panel
                        key={panel.id}
                        index={index}
                        state={stateOf(index)}
                        layer={count + 2 + index}
                    >
                        {panel.node}
                    </Panel>
                ))}

                <nav className="scrolly__nav" aria-label="Sections du site">
                    <ol>
                        {panels.map((panel, index) => (
                            <li key={panel.id}>
                                <button
                                    type="button"
                                    className={index === active ? 'is-active' : ''}
                                    aria-label={`Aller à : ${panel.label}`}
                                    aria-current={index === active ? 'true' : undefined}
                                    onClick={() => goTo(index)}
                                />
                            </li>
                        ))}
                    </ol>
                </nav>
            </div>

            {panels.map((panel, index) => (
                <div
                    key={panel.id}
                    ref={index === 0 ? stepRef : null}
                    className="scrolly__step"
                    style={{ top: `calc(${index} * var(--screen))` }}
                    aria-hidden="true"
                />
            ))}
        </div>
    )
}

export default Scrollytelling
